import React, { useState } from "react";
import { Link as RouterLink, NavLink } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import { clinic } from "../constants/clinic";

const menuItems = [
  { text: "ホーム", path: "/" },
  { text: "当院について", path: "/about" },
  { text: "施術内容", path: "/services" },
  { text: "アクセス", path: "/access" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeDrawer = () => setMobileOpen(false);

  const brand = (
    <Stack
      component={RouterLink}
      to="/"
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ color: "text.primary", textDecoration: "none", minWidth: 0 }}
      aria-label="わかば接骨院 ホーム"
    >
      <Box
        sx={{
          width: { xs: 42, md: 48 },
          height: { xs: 42, md: 48 },
          borderRadius: "50% 50% 50% 14px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "grid",
          placeItems: "center",
          transform: "rotate(-6deg)",
          flexShrink: 0,
        }}
      >
        <SpaRoundedIcon sx={{ transform: "rotate(6deg)", fontSize: 27 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            color: "primary.dark",
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.14em",
            lineHeight: 1.2,
          }}
        >
          愛知県瀬戸市
        </Typography>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
            fontSize: { xs: "1.16rem", sm: "1.35rem" },
            fontWeight: 700,
            letterSpacing: "0.08em",
            whiteSpace: "nowrap",
          }}
        >
          わかば接骨院
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <>
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          bgcolor: "#E8F0E8",
          color: "text.primary",
          py: 0.75,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="flex-end" spacing={3.5}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AccessTimeRoundedIcon sx={{ fontSize: 17, color: "primary.dark" }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                午前9時から12時 / 午後4時から8時（水・土は午前のみ）
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LocationOnRoundedIcon sx={{ fontSize: 17, color: "primary.dark" }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {clinic.station}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          top: 0,
          bgcolor: "rgba(250, 250, 249, 0.97)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid",
          borderColor: "#D6D3D1",
          color: "text.primary",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 80 } }}>
            <Box sx={{ flexGrow: 1 }}>{brand}</Box>

            <Stack
              component="nav"
              aria-label="メインメニュー"
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  end={item.path === "/"}
                  color="inherit"
                  sx={{
                    minHeight: 42,
                    px: 1.7,
                    fontSize: "0.91rem",
                    "&.active": { color: "primary.dark", bgcolor: "primary.light" },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                component="a"
                href={clinic.phoneHref}
                variant="contained"
                startIcon={<LocalPhoneRoundedIcon />}
                sx={{ ml: 1.25, px: 2.4 }}
              >
                {clinic.phone}
              </Button>
            </Stack>

            <IconButton
              aria-label="メニューを開く"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: "none" }, color: "primary.dark" }}
            >
              <MenuRoundedIcon fontSize="large" />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: "min(88vw, 360px)", bgcolor: "background.default" } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {brand}
            <IconButton aria-label="メニューを閉じる" onClick={closeDrawer}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 2.5 }} />
          <List component="nav" aria-label="モバイルメニュー" disablePadding>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={NavLink}
                to={item.path}
                end={item.path === "/"}
                onClick={closeDrawer}
                sx={{
                  borderRadius: 2,
                  mb: 0.75,
                  py: 1.25,
                  "&.active": { bgcolor: "primary.light", color: "primary.dark" },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: "1.05rem" }}
                />
              </ListItemButton>
            ))}
            <ListItemButton
              component={RouterLink}
              to="/contact"
              onClick={closeDrawer}
              sx={{ borderRadius: 2, mb: 0.75, py: 1.25 }}
            >
              <ListItemText
                primary="ご予約・お問い合わせ"
                primaryTypographyProps={{ fontWeight: 700, fontSize: "1.05rem" }}
              />
            </ListItemButton>
          </List>
          <Box
            sx={{
              mt: 3,
              p: 2.5,
              bgcolor: "#F5F5F4",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              color: "text.primary",
            }}
          >
            <Typography variant="body2">お電話でのご予約・ご相談</Typography>
            <Button
              component="a"
              href={clinic.phoneHref}
              fullWidth
              variant="contained"
              startIcon={<LocalPhoneRoundedIcon />}
              sx={{
                mt: 1.5,
                fontSize: "1.08rem",
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {clinic.phone}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Box
        sx={{
          position: "fixed",
          display: { xs: "grid", md: "none" },
          gridTemplateColumns: "1fr 1.35fr",
          left: 12,
          right: 12,
          bottom: 10,
          zIndex: (theme) => theme.zIndex.appBar + 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 999,
          p: 0.6,
          boxShadow: "0 12px 35px rgba(41, 37, 36, 0.16)",
        }}
      >
        <Button
          component={RouterLink}
          to="/access"
          startIcon={<LocationOnRoundedIcon />}
          color="inherit"
          sx={{ minHeight: 50, px: 1 }}
        >
          アクセス
        </Button>
        <Button
          component="a"
          href={clinic.phoneHref}
          variant="contained"
          startIcon={<LocalPhoneRoundedIcon />}
          sx={{ minHeight: 50, px: 1 }}
        >
          電話で予約
        </Button>
      </Box>
    </>
  );
};

export default Header;
